import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useAppLayout } from '@/context/AppLayoutContext';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { closeMobileBillModule } from '@/redux/modules/listing';
import { TRootState } from '@/redux/rootReducer';
import { getIsListingPublished } from '@/redux/selectors/listing/page';
import { getIsListingAllowedByWhitelist } from '@/redux/selectors/stayStates';
import { enableBodyScroll } from '@/utility/bodyScroll';

import AskOwnerButtonContainer from '../AskHostButton/AskHostButtonContainer';
import BillSectionMobileBarContainer from '../BillSection/BillSectionMobileBarContainer';
import css from './BillSection.module.css';
import BillSectionModal from './BillSectionModal/BillSectionModal';
import BillSectionUnpublished from './BillSectionUnpublished/BillSectionUnpublished';
import { BillSelectionStep } from './BillSelectionStep/BillSelectionStep';

type TBillSelectionProps = {
  showMessageButton: boolean;
  isAuto?: boolean;
};

export const BillSelection = ({ showMessageButton, isAuto }: TBillSelectionProps) => {
  const dispatch = useDispatch();
  const { isAboveDesktop } = useBreakpoint();
  const isListingAllowedByWhitelist = useSelector(getIsListingAllowedByWhitelist);
  const isPublished = useSelector(getIsListingPublished);
  const isMobileBillModuleOpen = useSelector<TRootState, boolean>(
    state => state.listing.isMobileBillModuleOpen,
  );
  const { headerHeight } = useAppLayout();
  const [stickyHeaderHeight, setStickyHeaderHeight] = useState(0);
  const billSectionRef = useRef<HTMLDivElement>(null);

  const handleCloseMobileBill = useCallback(
    (event?: React.MouseEvent) => {
      event?.preventDefault();

      enableBodyScroll();

      dispatch(closeMobileBillModule());
    },
    [dispatch],
  );

  useEffect(() => {
    if (isMobileBillModuleOpen && isAboveDesktop) {
      handleCloseMobileBill();
    }
  }, [isMobileBillModuleOpen, isAboveDesktop, handleCloseMobileBill]);

  // enable body scroll on unmount
  useEffect(() => () => enableBodyScroll(), []);

  useEffect(() => {
    const stickyHeaderElement = document.getElementById('listing-sticky-header');

    if (!stickyHeaderElement) return;

    const mutationObserver = new MutationObserver(([entry]) => {
      setStickyHeaderHeight(
        (entry?.target as HTMLElement)?.dataset.visible === 'true'
          ? (entry?.target as HTMLElement)?.getBoundingClientRect().height || 0
          : 0,
      );
    });

    mutationObserver.observe(stickyHeaderElement, {
      attributes: true,
      attributeFilter: ['data-visible'],
    });

    return () => mutationObserver.disconnect();
  }, []);

  return (
    <>
      <div
        ref={billSectionRef}
        className={css.billModule}
        data-open-mobile={isMobileBillModuleOpen}
        style={{ top: isAboveDesktop ? `${headerHeight + 36 + stickyHeaderHeight}px` : '0px' }}>
        <BillSectionModal onCloseMobile={handleCloseMobileBill}>
          {isPublished && isListingAllowedByWhitelist ? (
            <>
              <BillSelectionStep isAuto={isAuto} />
              {showMessageButton && <AskOwnerButtonContainer textButton={true} />}
            </>
          ) : (
            <BillSectionUnpublished />
          )}
        </BillSectionModal>
      </div>

      <div
        // Only visible on viewport > lg
        className={css.mobileBarContainer}
        // This is being used by Help menu in GTM to position the button properly
        data-sticky="true">
        <BillSectionMobileBarContainer />
      </div>
    </>
  );
};
